import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Building, Phone, Mail, User, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    company_contact: '',
    company_email: '',
    company_address: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    additional_emails: [],
    notes: ''
  });
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastPreview, setBroadcastPreview] = useState(null);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');

  const openBroadcastModal = async () => {
    setShowBroadcastModal(true);
    setBroadcastPreview(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/customers/broadcast/email-change/preview`);
      setBroadcastPreview(res.data);
    } catch {
      toast.error('Failed to load recipient preview');
    }
  };

  const handleSendBroadcast = async (testOnly) => {
    if (!testOnly && !window.confirm(`Send the email-change notice to ALL ${broadcastPreview?.unique_recipients || 0} customers? This cannot be undone.`)) {
      return;
    }
    setBroadcastSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/customers/broadcast/email-change`, {
        test_only: testOnly,
        custom_message: broadcastMessage,
      });
      toast.success(testOnly
        ? `Test email sent to first customer (${res.data.sent} sent, ${res.data.failed} failed)`
        : `Sent to ${res.data.sent} customers (${res.data.failed} failed)`);
      if (!testOnly) {
        setShowBroadcastModal(false);
        setBroadcastMessage('');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Broadcast failed');
    } finally {
      setBroadcastSending(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [searchTerm, customers]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers/`);
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    if (!searchTerm) {
      setFilteredCustomers(customers);
      return;
    }
    
    const filtered = customers.filter(cust =>
      (cust.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cust.company_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cust.contact_person || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCustomers(filtered);
  };

  const openAddModal = () => {
    setEditingCustomer(null);
    setFormData({
      company_name: '',
      company_contact: '',
      company_email: '',
      company_address: '',
      contact_person: '',
      contact_phone: '',
      contact_email: '',
      additional_emails: [],
      notes: ''
    });
    setShowAddModal(true);
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      company_name: customer.company_name || '',
      company_contact: customer.company_contact || '',
      company_email: customer.company_email || '',
      company_address: customer.company_address || '',
      contact_person: customer.contact_person || '',
      contact_phone: customer.contact_phone || '',
      contact_email: customer.contact_email || '',
      additional_emails: customer.additional_emails || [],
      notes: customer.notes || ''
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        additional_emails: (formData.additional_emails || []).map((e) => e.trim()).filter((e) => e.includes('@')),
      };
      if (editingCustomer) {
        await axios.put(`${BACKEND_URL}/api/customers/${editingCustomer.id}`, payload);
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/customers/`, { ...payload, source: 'manual' });
        toast.success('Customer added successfully');
      }
      setShowAddModal(false);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to save customer');
      console.error(error);
    }
  };

  const deleteCustomer = async (customerId, customerName) => {
    if (!window.confirm(`Are you sure you want to delete ${customerName}?`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/customers/${customerId}`);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'booking':
        return <Badge className="bg-blue-100 text-blue-800">Booking</Badge>;
      case 'quotation':
        return <Badge className="bg-purple-100 text-purple-800">Quotation</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Manual</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Customer Database</h1>
              <p className="text-gray-600 mt-1">Manage your customer records</p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={openBroadcastModal} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="email-broadcast-btn">
                <Mail size={18} className="mr-2" />
                Notify Email Change
              </Button>
              <Button onClick={openAddModal} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-customer-btn">
                <Plus size={20} className="mr-2" />
                Add Customer
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Stats */}
      <div className="container mx-auto px-4 py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative w-full md:w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  placeholder="Search by company, email, or contact..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-customers"
                />
              </div>
              <div className="flex gap-4">
                <div className="bg-blue-50 px-4 py-2 rounded-lg">
                  <span className="text-2xl font-bold text-blue-800">{customers.length}</span>
                  <span className="text-blue-600 ml-2">Total Customers</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer List */}
      <div className="container mx-auto px-4 pb-8">
        {filteredCustomers.length === 0 ? (
          <Card>
            <CardContent className="py-20 text-center">
              <Building size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No customers found</p>
              <p className="text-gray-400 mt-2">
                {searchTerm ? 'Try adjusting your search' : 'Customers will be auto-recorded from bookings'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCustomers.map((customer) => (
              <Card key={customer.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900">{customer.company_name}</h3>
                      {getSourceBadge(customer.source)}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(customer)}
                        className="text-blue-600"
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteCustomer(customer.id, customer.company_name)}
                        className="text-red-600"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {customer.company_email && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail size={14} />
                        <a href={`mailto:${customer.company_email}`} className="hover:text-blue-600">
                          {customer.company_email}
                        </a>
                      </div>
                    )}
                    {(customer.additional_emails || []).map((em, i) => (
                      <div key={i} className="flex items-center gap-2 text-gray-600" data-testid={`customer-extra-email-${i}`}>
                        <Mail size={14} className="text-emerald-600" />
                        <a href={`mailto:${em}`} className="hover:text-blue-600">{em}</a>
                        <span className="text-xs text-gray-400">(cc)</span>
                      </div>
                    ))}
                    {customer.company_contact && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone size={14} />
                        <a href={`tel:${customer.company_contact}`} className="hover:text-blue-600">
                          {customer.company_contact}
                        </a>
                      </div>
                    )}
                    {customer.contact_person && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <User size={14} />
                        <span>{customer.contact_person}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t text-xs text-gray-400">
                    Added: {formatDate(customer.created_at)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
            <DialogDescription>
              {editingCustomer ? 'Update customer information' : 'Enter the customer details below'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_contact">Company Phone</Label>
                <Input
                  id="company_contact"
                  value={formData.company_contact}
                  onChange={(e) => setFormData({ ...formData, company_contact: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_email">Company Email</Label>
                <Input
                  id="company_email"
                  type="email"
                  value={formData.company_email}
                  onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_address">Company Address</Label>
              <Input
                id="company_address"
                value={formData.company_address}
                onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
              />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Additional Emails (Accounts / Admin)</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData({ ...formData, additional_emails: [...(formData.additional_emails || []), ''] })}
                  data-testid="add-email-slot-btn"
                >
                  <Plus size={14} className="mr-1" /> Add Email
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-2">Invoices emailed to this customer are CC'd to every email here.</p>
              {(formData.additional_emails || []).map((em, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <Input
                    type="email"
                    value={em}
                    placeholder="e.g., accounts@company.com"
                    onChange={(e) => {
                      const next = [...formData.additional_emails];
                      next[idx] = e.target.value;
                      setFormData({ ...formData, additional_emails: next });
                    }}
                    data-testid={`additional-email-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="text-red-600 shrink-0"
                    onClick={() => setFormData({ ...formData, additional_emails: formData.additional_emails.filter((_, i) => i !== idx) })}
                    data-testid={`remove-email-${idx}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Contact Person</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Name</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <Label htmlFor="contact_email">Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingCustomer ? 'Update' : 'Add'} Customer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Broadcast Email Change Dialog */}
      <Dialog open={showBroadcastModal} onOpenChange={setShowBroadcastModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <Mail size={20} /> Notify Customers — Email Address Change
            </DialogTitle>
            <DialogDescription>
              Send a one-time professional notice to all customers letting them know your email has changed from
              <span className="font-mono text-red-600"> escaynor_ng@njklogisticsservices.com</span> to
              <span className="font-mono text-green-700"> inquiry@njklogistics.com</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="bg-orange-50 border border-orange-200 rounded-md p-3 text-sm">
              {broadcastPreview ? (
                <>
                  <p className="text-orange-900">
                    <strong>{broadcastPreview.unique_recipients}</strong> unique customer email addresses will receive this notice.
                  </p>
                  {broadcastPreview.sample.length > 0 && (
                    <div className="mt-2 text-xs text-orange-700">
                      Sample: {broadcastPreview.sample.slice(0, 3).map(s => s.email).join(', ')}
                      {broadcastPreview.unique_recipients > 3 && ` +${broadcastPreview.unique_recipients - 3} more`}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-orange-700">Loading recipient count…</p>
              )}
            </div>

            <div>
              <Label htmlFor="broadcast-msg">Optional message to add (e.g., "Please update your records")</Label>
              <Textarea
                id="broadcast-msg"
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Leave blank for the default email body…"
                className="mt-1"
                rows={3}
                data-testid="broadcast-message"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
              💡 <strong>Tip:</strong> Click "Send Test" first — it sends only to the first customer in your list so you can preview how the email looks. Then click "Send to All".
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setShowBroadcastModal(false)} disabled={broadcastSending}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSendBroadcast(true)}
              disabled={broadcastSending || !broadcastPreview}
              data-testid="broadcast-test-btn"
            >
              {broadcastSending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
              Send Test (1 recipient)
            </Button>
            <Button
              onClick={() => handleSendBroadcast(false)}
              disabled={broadcastSending || !broadcastPreview || broadcastPreview.unique_recipients === 0}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="broadcast-send-btn"
            >
              {broadcastSending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
              Send to All {broadcastPreview ? `(${broadcastPreview.unique_recipients})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
